import React from 'react';
import { useMetaTags } from '../hooks/useMetaTags';
export function PrivacyPolicyPage() {
  useMetaTags({
    title: 'Privacy Policy | FindOnlineDegrees',
    description: 'Privacy policy for FindOnlineDegrees.org'
  });
  return <div className="min-h-screen bg-gray-100" data-id="element-1146">
      {/* Hero Section */}
      <section className="w-full bg-[#1a2332] text-white py-16 px-4" data-id="element-1147">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1148">
          <h1 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-1149">
            Privacy Policy
          </h1>
          <p className="text-gray-300 text-sm" data-id="element-1150">Last updated: January 2026</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="max-w-4xl mx-auto px-4 py-12" data-id="element-1151">
        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 space-y-8" data-id="element-1152">
          <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1153">
            <p data-id="element-1154">
              This privacy notice for FindOnlineDegrees.org describes how and
              why we might collect, store, use, and/or share your information
              when you use our services, such as when you:
            </p>
            <ul className="list-disc pl-6 space-y-2" data-id="element-1155">
              <li data-id="element-1156">
                Visit our website, or any website of ours that links to this
                privacy notice
              </li>
              <li data-id="element-1157">
                Engage with us in other related ways, including any sales,
                marketing, or events
              </li>
            </ul>
            <p className="font-semibold" data-id="element-1158">
              Questions or concerns? Reading this privacy notice will help you
              understand your privacy rights and choices. If you do not agree
              with our policies and practices, please do not use our services.
            </p>
          </div>

          {/* Summary of Key Points */}
          <div data-id="element-1159">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1160">
              SUMMARY OF KEY POINTS
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1161">
              <ul className="list-disc pl-6 space-y-2" data-id="element-1162">
                <li data-id="element-1163">
                  <strong data-id="element-1164">What personal information do we process?</strong> When
                  you visit, use, or navigate our services, we may process
                  personal information depending on how you interact with us,
                  the choices you make, and the features you use.
                </li>
                <li data-id="element-1165">
                  <strong data-id="element-1166">Do we process sensitive personal information?</strong>{' '}
                  We do not process sensitive personal information.
                </li>
                <li data-id="element-1167">
                  <strong data-id="element-1168">Do we receive information from third parties?</strong>{' '}
                  We may receive technical and advertising data from third
                  parties, including advertising platforms and analytics
                  providers.
                </li>
                <li data-id="element-1169">
                  <strong data-id="element-1170">How do we process your information?</strong> To
                  provide, improve, and administer our services, communicate
                  with you, ensure security and fraud prevention, comply with
                  law, and with your consent where required.
                </li>
                <li data-id="element-1171">
                  <strong data-id="element-1172">
                    When and with whom do we share personal information?
                  </strong>{' '}
                  Only in specific circumstances (e.g., business transfers or
                  service providers).
                </li>
                <li data-id="element-1173">
                  <strong data-id="element-1174">How do we keep your information safe?</strong> With
                  organizational and technical measures, but no method is 100%
                  secure.
                </li>
                <li data-id="element-1175">
                  <strong data-id="element-1176">What are your rights?</strong> Depending on your
                  location, you may have rights under applicable privacy laws.
                </li>
                <li data-id="element-1177">
                  <strong data-id="element-1178">How do you exercise your rights?</strong> You can
                  exercise rights by contacting us (see Section 12).
                </li>
              </ul>
            </div>
          </div>

          {/* Table of Contents */}
          <div data-id="element-1179">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1180">
              TABLE OF CONTENTS
            </h2>
            <ol className="list-decimal pl-6 space-y-2 text-gray-700" data-id="element-1181">
              <li data-id="element-1182">WHAT INFORMATION DO WE COLLECT?</li>
              <li data-id="element-1183">HOW DO WE PROCESS YOUR INFORMATION?</li>
              <li data-id="element-1184">SMS / TEXT MESSAGE COMMUNICATIONS</li>
              <li data-id="element-1185">WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?</li>
              <li data-id="element-1186">DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?</li>
              <li data-id="element-1187">HOW LONG DO WE KEEP YOUR INFORMATION?</li>
              <li data-id="element-1188">HOW DO WE KEEP YOUR INFORMATION SAFE?</li>
              <li data-id="element-1189">DO WE COLLECT INFORMATION FROM MINORS?</li>
              <li data-id="element-1190">WHAT ARE YOUR PRIVACY RIGHTS?</li>
              <li data-id="element-1191">CONTROLS FOR DO-NOT-TRACK FEATURES</li>
              <li data-id="element-1192">DO WE MAKE UPDATES TO THIS NOTICE?</li>
              <li data-id="element-1193">HOW CAN YOU CONTACT US ABOUT THIS NOTICE?</li>
            </ol>
          </div>

          {/* 1. What Information Do We Collect */}
          <div data-id="element-1194">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1195">
              1. WHAT INFORMATION DO WE COLLECT?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1196">
              <h3 className="font-bold text-lg" data-id="element-1197">
                Personal information you disclose to us
              </h3>
              <p data-id="element-1198">
                We collect personal information that you voluntarily provide to
                us when you express an interest in obtaining information about
                us or our services, when you participate in activities on the
                services, or otherwise when you contact us.
              </p>
              <p data-id="element-1199">
                <strong data-id="element-1200">Personal Information Provided by You.</strong> The
                personal information we collect may include:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1201">
                <li data-id="element-1202">Names</li>
                <li data-id="element-1203">Phone numbers</li>
                <li data-id="element-1204">Email addresses</li>
                <li data-id="element-1205">Mailing addresses</li>
                <li data-id="element-1206">Education level and interests</li>
                <li data-id="element-1207">Program preferences</li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-1208">
                Information automatically collected
              </h3>
              <p data-id="element-1209">
                We automatically collect certain information when you visit,
                use, or navigate the services. This information does not reveal
                your specific identity but may include device and usage
                information, such as:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1210">
                <li data-id="element-1211">IP address</li>
                <li data-id="element-1212">Browser and device characteristics</li>
                <li data-id="element-1213">Operating system</li>
                <li data-id="element-1214">Language preferences</li>
                <li data-id="element-1215">Referring URLs</li>
                <li data-id="element-1216">Device name and type</li>
                <li data-id="element-1217">Pages viewed and time spent</li>
                <li data-id="element-1218">Clickstream data</li>
              </ul>
            </div>
          </div>

          {/* 2. How Do We Process Your Information */}
          <div data-id="element-1219">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1220">
              2. HOW DO WE PROCESS YOUR INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1221">
              <p data-id="element-1222">
                We process your personal information for a variety of reasons,
                including:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1223">
                <li data-id="element-1224">To facilitate account creation and authentication</li>
                <li data-id="element-1225">To deliver and facilitate delivery of services to you</li>
                <li data-id="element-1226">To respond to user inquiries and offer support</li>
                <li data-id="element-1227">To send administrative information to you</li>
                <li data-id="element-1228">To fulfill and manage your requests</li>
                <li data-id="element-1229">To enable user-to-user communications</li>
                <li data-id="element-1230">
                  To request feedback and contact you about your use of our
                  services
                </li>
                <li data-id="element-1231">To send you marketing and promotional communications</li>
                <li data-id="element-1232">To deliver targeted advertising to you</li>
                <li data-id="element-1233">
                  To protect our services and investigate potential violations
                </li>
                <li data-id="element-1234">
                  To identify usage trends and determine the effectiveness of
                  campaigns
                </li>
                <li data-id="element-1235">To save or protect an individual's vital interest</li>
                <li data-id="element-1236">To comply with our legal obligations</li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-1237">Email Communications</h3>
              <p data-id="element-1238">
                When you submit a form on our website, you may consent to
                receive marketing and promotional emails from
                FindOnlineDegrees.org (operated by Media Matchers LLC) and its
                educational partners at the email address you provide. Marketing
                emails are sent from contact@findonlinedegrees.org. Every
                marketing email includes an unsubscribe link. You may opt out at
                any time and your request will be honored within 10 business
                days. Opting out of marketing email does not affect
                transactional or administrative messages related to a specific
                inquiry you have submitted.
              </p>
            </div>
          </div>

          {/* 3. SMS / Text Message Communications */}
          <div data-id="element-1239">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1240">
              3. SMS / TEXT MESSAGE COMMUNICATIONS
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1241">
              <h3 className="font-bold text-lg" data-id="element-1242">How We Collect SMS Consent</h3>
              <p data-id="element-1243">
                When you submit a form on our website, you may expressly consent
                to receive autodialed and/or pre-recorded marketing text
                messages (SMS/MMS) from FindOnlineDegrees.org (operated by Media
                Matchers LLC) and its educational partners at the mobile phone
                number you provide. This consent is obtained at the point of
                form submission and is documented with the date, time, IP
                address, and page URL at which consent was given.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1244">
                How We Use Your Phone Number for SMS
              </h3>
              <p data-id="element-1245">
                If you have provided consent, we may send you text messages for
                purposes including:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1246">
                <li data-id="element-1247">
                  Information about educational programs matching your interests
                </li>
                <li data-id="element-1248">Follow-up communications related to your inquiry</li>
                <li data-id="element-1249">
                  Promotional offers and marketing messages from us or our
                  educational partners
                </li>
              </ul>

              <h3 className="font-bold text-lg mt-6" data-id="element-1250">
                Consent Is Not Required
              </h3>
              <p data-id="element-1251">
                Your consent to receive SMS/MMS messages is never a condition of
                using our services or receiving information. You may choose not
                to provide consent and still access our website and resources.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1252">
                Message Frequency and Rates
              </h3>
              <p data-id="element-1253">
                Message frequency varies based on your interactions and
                preferences. Standard message and data rates may apply depending
                on your carrier and plan.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1254">How to Opt Out</h3>
              <p data-id="element-1255">
                You may revoke your consent and opt out of SMS communications at
                any time by:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1256">
                <li data-id="element-1257">Replying STOP to any text message you receive from us</li>
                <li data-id="element-1258">
                  Contacting us directly using the information in Section 12
                </li>
              </ul>
              <p data-id="element-1259">
                Opt-out requests will be honored promptly. After opting out, you
                may receive a single confirmation message. You will not receive
                further SMS marketing messages unless you provide new consent.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1260">For Assistance</h3>
              <p data-id="element-1261">
                Reply HELP to any text message for support information, or
                contact us directly using the information in Section 12.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1262">
                Third-Party SMS Senders
              </h3>
              <p data-id="element-1263">
                When you consent to receive communications from our educational
                partners, those partners may contact you directly via SMS under
                their own messaging programs and privacy policies. We encourage
                you to review the privacy policies of any educational
                institution or partner that contacts you.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1264">Record Retention</h3>
              <p data-id="element-1265">
                We retain records of SMS consent, including timestamp, IP
                address, form URL, and submitted form data, in accordance with
                our general data retention practices described in Section 6.
              </p>
            </div>
          </div>

          {/* 4. When and With Whom Do We Share */}
          <div data-id="element-1266">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1267">
              4. WHEN AND WITH WHOM DO WE SHARE YOUR PERSONAL INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1268">
              <p data-id="element-1269">We may share information in the following situations:</p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1270">
                <li data-id="element-1271">
                  <strong data-id="element-1272">Business Transfers:</strong> In connection with, or
                  during negotiations of, any merger, sale of company assets,
                  financing, or acquisition.
                </li>
                <li data-id="element-1273">
                  <strong data-id="element-1274">Educational Institutions:</strong> We share your
                  information with schools and educational partners that match
                  your interests.
                </li>
                <li data-id="element-1275">
                  <strong data-id="element-1276">Service Providers:</strong> With third-party vendors,
                  service providers, contractors, or agents who perform services
                  for us.
                </li>
                <li data-id="element-1277">
                  <strong data-id="element-1278">Advertising Partners:</strong> With advertising
                  partners to deliver targeted ads.
                </li>
                <li data-id="element-1279">
                  <strong data-id="element-1280">Legal Requirements:</strong> When required by law or
                  to protect rights, safety, and property.
                </li>
              </ul>
            </div>
          </div>

          {/* 5. Cookies and Tracking */}
          <div data-id="element-1281">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1282">
              5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1283">
              <p data-id="element-1284">
                Yes, we use cookies and similar tracking technologies to access
                or store information. See our Cookie Notice for more details
                about how we use cookies.
              </p>
            </div>
          </div>

          {/* 6. How Long Do We Keep */}
          <div data-id="element-1285">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1286">
              6. HOW LONG DO WE KEEP YOUR INFORMATION?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1287">
              <p data-id="element-1288">
                We keep your information for as long as necessary to fulfill the
                purposes outlined in this privacy notice unless otherwise
                required by law. When we have no ongoing legitimate business
                need to process your personal information, we will delete or
                anonymize it.
              </p>
            </div>
          </div>

          {/* 7. How Do We Keep Information Safe */}
          <div data-id="element-1289">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1290">
              7. HOW DO WE KEEP YOUR INFORMATION SAFE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1291">
              <p data-id="element-1292">
                We have implemented appropriate technical and organizational
                security measures designed to protect the security of any
                personal information we process. However, no electronic
                transmission over the internet or information storage technology
                can be guaranteed to be 100% secure.
              </p>
            </div>
          </div>

          {/* 8. Minors */}
          <div data-id="element-1293">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1294">
              8. DO WE COLLECT INFORMATION FROM MINORS?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1295">
              <p data-id="element-1296">
                We do not knowingly solicit data from or market to children
                under 18 years of age. If you become aware that a child has
                provided us with personal information, please contact us. If we
                learn we have collected personal information from a child under
                18, we will delete that information.
              </p>
            </div>
          </div>

          {/* 9. Privacy Rights */}
          <div data-id="element-1297">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1298">
              9. WHAT ARE YOUR PRIVACY RIGHTS?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1299">
              <p data-id="element-1300">
                Depending on your location, you may have the following rights:
              </p>
              <ul className="list-disc pl-6 space-y-2" data-id="element-1301">
                <li data-id="element-1302">The right to access your personal information</li>
                <li data-id="element-1303">
                  The right to request correction of your personal information
                </li>
                <li data-id="element-1304">
                  The right to request deletion of your personal information
                </li>
                <li data-id="element-1305">The right to opt out of marketing communications</li>
                <li data-id="element-1306">
                  The right to opt out of the sale or sharing of personal
                  information (where applicable)
                </li>
              </ul>
              <p className="mt-4" data-id="element-1307">
                To exercise these rights, please contact us using the
                information in Section 12.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1308">
                Opt Out of Marketing Email
              </h3>
              <p data-id="element-1309">
                To opt out of marketing email: Click the unsubscribe link in any
                marketing email you receive from us, or contact us at the
                address in Section 12. Requests are processed within 10 business
                days.
              </p>

              <h3 className="font-bold text-lg mt-6" data-id="element-1310">
                California Residents — Opt Out of Sale/Sharing
              </h3>
              <p data-id="element-1311">
                California residents — opt out of sale/sharing of personal
                information: To exercise your right to opt out under CCPA/CPRA,
                contact us at the address in Section 12 with the subject line
                "California Privacy Opt-Out." We will process your request
                within 15 business days.
              </p>
            </div>
          </div>

          {/* 10. Do Not Track */}
          <div data-id="element-1312">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1313">
              10. CONTROLS FOR DO-NOT-TRACK FEATURES
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1314">
              <p data-id="element-1315">
                Most web browsers and some mobile operating systems include a
                Do-Not-Track ("DNT") feature or setting you can activate to
                signal your privacy preference. At this stage, no uniform
                technology standard for recognizing and implementing DNT signals
                has been finalized.
              </p>
            </div>
          </div>

          {/* 11. Updates */}
          <div data-id="element-1316">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1317">
              11. DO WE MAKE UPDATES TO THIS NOTICE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1318">
              <p data-id="element-1319">
                Yes, we will update this notice as necessary to stay compliant
                with relevant laws. The updated version will be indicated by an
                updated "Last updated" date at the top of this privacy notice.
              </p>
            </div>
          </div>

          {/* 12. Contact Us */}
          <div data-id="element-1320">
            <h2 className="text-2xl font-bold text-[#3b82f6] mb-4" data-id="element-1321">
              12. HOW CAN YOU CONTACT US ABOUT THIS NOTICE?
            </h2>
            <div className="space-y-4 text-gray-700 leading-relaxed" data-id="element-1322">
              <p data-id="element-1323">
                If you have questions or comments about this notice, please
                contact us at:
              </p>
              <p className="font-semibold" data-id="element-1324">Media Matchers LLC</p>
              <p data-id="element-1325">
                1865 Chestnut Street
                <br data-id="element-1326" />
                San Francisco, CA 94123
              </p>
              <p data-id="element-1327">
                Email:{' '}
                <a href="mailto:contact@findonlinedegrees.org" className="text-[#3b82f6] underline hover:text-blue-800" data-id="element-1328">
                  contact@findonlinedegrees.org
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>;
}